import { ChangeDetectionStrategy, Component, computed, inject, input, linkedSignal, signal } from "@angular/core";
import { MatchType, UpdateStrategyTopOfSearchRankingsActionEnum } from "@front/m19-api-client";
import { StrategyEx } from "@front/m19-models";
import { SpStrategyService } from "@front/m19-services";
import { IToggleComponent, ITooltipDirective, ToasterService } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { keywordRankingAvailableFor } from "@m19-board/tracking/KeywordRankingAvailability";

import { SettingRowComponent } from "../setting-row/setting-row.component";
@Component({
  selector: "app-toggle-tosro",
  imports: [IToggleComponent, TranslocoDirective, SettingRowComponent, ITooltipDirective],
  template: `@if (spStrategy(); as strategy) {
    <app-setting-row *transloco="let t">
      <ng-container label>
        {{ t("sp-strategy-group-page.top_of_search_rankings_optimizer") }}
      </ng-container>
      <IToggle
        [checked]="isTosroActivated()"
        [disabled]="isTosroDisabled()"
        [iTooltip]="tosroTooltip()"
        [loading]="updateTosroLoading()"
        (checkedChange)="updateTosro(!!$event)"
      />
    </app-setting-row>
  }`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToggleTosroComponent {
  private readonly spStrategyService = inject(SpStrategyService);
  private readonly toastrService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);

  readonly strategy = input.required<StrategyEx>();
  readonly organizationId = input.required<number>();
  readonly isReadOnly = input.required<boolean>();

  readonly spStrategy = computed(() => {
    const strategy = this.strategy();
    return strategy.isSpStrategy() ? strategy : undefined;
  });

  private readonly tosroSupportedMarketplace = computed(() => {
    const strategy = this.spStrategy();
    if (!strategy) return false;
    return keywordRankingAvailableFor(strategy.marketplace);
  });
  private readonly tosroHasExactTargeting = computed(() => {
    return this.spStrategy()?.targetings?.some((t) => t.matchType === MatchType.exact) ?? false;
  });
  readonly isTosroDisabled = computed(
    () => !this.tosroSupportedMarketplace() || this.isReadOnly() || !this.tosroHasExactTargeting(),
  );
  readonly isTosroActivated = linkedSignal(() => {
    const s = this.spStrategy();
    return (s?.topOfSearchRankings?.length ?? 0) > 0;
  });

  readonly tosroTooltip = computed(() => {
    if (this.isReadOnly()) {
      return this.translocoService.translate("sp-strategy-group-page.you_do_not_have_the_permission_to_activate_tosro");
    } else if (!this.tosroSupportedMarketplace()) {
      return this.translocoService.translate("sp-strategy-group-page.tosro_not_supported_for_this_marketplace");
    } else if (!this.tosroHasExactTargeting()) {
      return this.translocoService.translate("sp-strategy-group-page.tosro_only_for_kw");
    }
    return this.translocoService.translate("sp-strategy-group-page.activated_keywords_tracked");
  });
  readonly updateTosroLoading = signal(false);

  updateTosro(enable: boolean): void {
    const strategy = this.spStrategy();
    if (!strategy) {
      return;
    }
    this.updateTosroLoading.set(true);
    const keywords: string[] = enable
      ? strategy.targetings.filter((t) => t.matchType === MatchType.exact).map((t) => t.targetingValue!)
      : strategy.topOfSearchRankings.map((t) => t.keyword!);
    this.spStrategyService
      .updateTopOfSearchRankings(
        strategy,
        this.organizationId()!,
        keywords,
        enable ? UpdateStrategyTopOfSearchRankingsActionEnum.ADD : UpdateStrategyTopOfSearchRankingsActionEnum.DELETE,
      )
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.translocoService.translate(
              `sp-strategy-group-page.top_of_search_rankings_optimizer_${enable ? "activated" : "deactivated"}`,
            ),
          );
          this.updateTosroLoading.set(false);
          this.isTosroActivated.update((a) => !a);
        },
        error: (error) => {
          this.toastrService.error(
            `Error: ${error}`,
            this.translocoService.translate("sp-strategy-group-page.top_of_search_rankings_optimizer_update_error"),
          );
          this.updateTosroLoading.set(false);
        },
      });
  }
}
